'use client';

import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { CSSProperties, ReactNode, RefObject, useMemo, useRef, useState } from 'react';
import Button, { ButtonProps } from '../Button/Button';
import styles from './Carousel.module.scss';

const IconButton = ({ children, style, ...props }: ButtonProps) => (
  <Button
    style={{
      borderRadius: 1000,
      boxSizing: 'content-box',
      width: 24,
      height: 24,
      padding: 8,
      ...style,
    }}
    {...props}
  >
    {children}
  </Button>
);

const modulo = (n: number, d: number) => ((n % d) + d) % d;

const getScrollProgress = (el: HTMLElement | null) =>
  el ? el.scrollLeft / (el.scrollWidth - el.clientWidth) : 0;

const setScrollProgress = (el: HTMLElement | null, progress: number, options?: ScrollOptions) => {
  if (!el) return;
  el.scrollTo({ left: progress * (el.scrollWidth - el.clientWidth), ...options });
};

export type CarouselImage = {
  image?: ReactNode;
  thumbnail?: ReactNode;
  background?: CSSProperties['background'];
  caption?: ReactNode | string;
};

export type CarouselProps = {
  height?: CSSProperties['height'];
  images?: CarouselImage[];
};

const Carousel: React.FC<CarouselProps> = ({ height, images = [] }) => {
  const numSlides = images.length;

  // const [slide, setSlide] = useState<number>(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  const [scrollProgress, setScrollProgressState] = useState<number>(0);
  const slide = useMemo(
    () => Math.round(scrollProgress * (numSlides - 1)),
    [scrollProgress, numSlides]
  );
  const setSlide = (slide: number | ((current: number) => number)) => {
    const currentSlide = Math.round(scrollProgress * (numSlides - 1));
    const newSlide = typeof slide === 'number' ? slide : slide(currentSlide);
    const progress = newSlide / (numSlides - 1);
    setScrollProgress(carouselRef.current, progress, { behavior: 'smooth' });
  };

  const prevSlide = () => setSlide((cur) => modulo(cur - 1, numSlides));
  const nextSlide = () => setSlide((cur) => modulo(cur + 1, numSlides));

  const handleScroll =
    (sync: RefObject<null | HTMLElement>) => (e: React.UIEvent<HTMLDivElement>) => {
      if (!e.currentTarget || !sync.current) return;
      const progress = getScrollProgress(e.currentTarget);
      setScrollProgress(sync.current, progress);
      setScrollProgressState(progress);
    };

  return (
    <div className={styles.carouselContainer}>
      <div
        className={styles.carousel}
        style={{ height }}
        ref={carouselRef}
        onScroll={handleScroll(thumbnailContainerRef)}
      >
        {images.map(({ image, background, caption }, idx) => (
          <motion.div
            key={idx}
            className={styles.slide}
            style={{ background }}
            initial="rest"
            whileHover="hovered"
          >
            <div className={styles.imageContainer}>
              {image}
              <motion.div
                className={styles.captionBackdrop}
                variants={{ rest: { opacity: 0, y: '100px' }, hovered: { opacity: 1, y: 0 } }}
                transition={{ bounce: 0 }}
              >
                <div className={styles.caption}>{caption}</div>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className={styles.controls}>
        <IconButton className={styles.controlButton} onClick={prevSlide}>
          <ChevronLeft />
        </IconButton>
        <div className={styles.thumbnailContainer} ref={thumbnailContainerRef}>
          <div className={styles.thumbnails}>
            {images.map((image, idx) => (
              <div
                key={idx}
                className={clsx(styles.thumbnail, { [styles.current]: idx === slide })}
                onClick={() => setSlide(idx)}
              >
                {image.thumbnail || image.image}
              </div>
            ))}
          </div>
        </div>
        <IconButton className={styles.controlButton} onClick={nextSlide}>
          <ChevronRight />
        </IconButton>
      </div>
    </div>
  );
};

export default Carousel;
